import React, {
    useEffect,
    useState,
    useContext,
    useCallback,
    useReducer,
} from 'react';
import { useLocation, Outlet } from 'react-router-dom';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import { makeStyles, ThemeProvider as MuiThemeProvider } from '@mui/styles';
import { ThemeProvider } from '@mui/material/styles';
import { StyledEngineProvider } from '@mui/styled-engine';
import CssBaseline from '@mui/material/CssBaseline';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
// import MomentUtils from '@date-io/moment';

import Themes from '../../data/themes';

import SideNav from '../../components/sidenav/mc2';
import TopNav from '../../components/topnav';
import Breadcrumbs from '../../components/breadcrumbs';
import Notifications from '../../components/notifications';
import HeaderMessage from '../../components/messages/header';
import FooterMessage, { Copyright } from '../../components/messages/footer';

import SystemService from '../../data/system-service';
import NotifyService from '../../data/notify-service';
import DataContext from '../../data/data-context';
import AssetsService from '../../data/assets-service';
import TagsService from '../../data/tags-service';
import SensorsService from '../../data/sensors-service';
import IncidentService from '../../data/incident-service';
import MetadataService, { DefaultMapping as DefaultMtdtMappings } from '../../data/metadata-service';
import AlertsService, { DefaultMapping as DefaultAlertMappings } from '../../data/alerts-service';
import CMCsDataService, { CmcContext } from '../../data/cmcs-service';
import Auth, { ProfileContext, UISettings } from '../../data/auth-service';
import { UpdateReducer, TranslateTheme, UpdateAllReducer, IsSameMappings } from '../../libraries/helpers';
import { blue, neutrals1 } from '../../data/colors';
import { NoticeToast } from '../cmc/app';

const useStyles = makeStyles(theme => ({
    content: {
        flexGrow: 1,
        paddingLeft: theme.spacing(5),
        paddingRight: theme.spacing(3),
        paddingBottom: theme.spacing(3),
        transition: 'margin-left 500ms',
    },

    // this only needs to be defined once in the app
    '@global': {
        '.react-codemirror2': {
            '& .cm-s-material': {
                height: 'auto',
                border: `solid 1px ${theme.palette.primary.dark}`,
                backgroundColor: blue[900],
                padding: 5,
                fontSize: 15,
            },
            '& .cm-s-xq-light': {
                height: 'auto',
                border: `solid 1px ${theme.palette.primary.dark}`,
                backgroundColor: neutrals1[0],
                padding: 5,
                fontSize: 15,
            },

            '& .CodeMirror.CodeMirror-focused': {
                border: `solid 1px ${theme.palette.primary.main}`,
            },
        },
    },
}));

const AppWrapper = () => {
    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={Themes.DarkTheme}>
                <MuiThemeProvider theme={Themes.DarkTheme}>
                    <App />
                </MuiThemeProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

const App = (props) => {
    const classes = useStyles();
    const location = useLocation();

    const [systemSettings, setSystemSettings] = useReducer(UpdateReducer, { object: {} });
    const [systemHealth, setSystemHealth] = useReducer(UpdateAllReducer, { health_metrics: {}, health_status: {} });

    const [mitreCategories, setMitreCategories] = useState([]);
    const [mitreCategoriesMap, setMitreCategoriesMap] = useState({});
    const [sensorNames, setSensorNames] = useState({});
    const [offset, setOffset] = useState(135);
    const [messages, setMessages] = useState({
        header: '',
        footer: '',
    });

    const [cmc, setCmc] = useState();
    const [showIP, setShowIP] = useState(false);
    const [assets, setAssets] = useState({});
    const [tags, setTags] = useState({});
    const [showTag, setShowTag] = useState(false);
    const [lookups, setLookups] = useState([]);
    const [filehashLookups, setFilehashLookups] = useState([]);
    const [theme, setTheme] = useState(Themes.DarkTheme);

    const { updateProfile, getProfile } = useContext(ProfileContext);

    const cmcPerms = Auth.permissions.get('CMC');
    const incidentPerms = Auth.permissions.get('Incidents');
    const assetPerms = Auth.permissions.get('Assets');
    const tagPerms = Auth.permissions.get('Tags');
    const healthPerms = Auth.permissions.get('SystemHealth');

    const setSidenav = (s, m) => {
        updateProfile('nav', { ...s, ...m });
        _setSidenav({ ...s, ...m });
    };

    // const [sidenav, _setSidenav] = useState({});
    const [sidenav, _setSidenav] = useReducer(UpdateReducer, { collapsed: false, panels: {} });

    // mapping dispatchers, update service, stash in profile and finally return state
    const setAndStashAlertMappings = (s, m) => {
        if (m && IsSameMappings(s, m)) return s;
        const mappings = AlertsService.doColumnMapping(m);
        const profile = getProfile('alerts');
        if (!IsSameMappings(mappings, (profile.mappings || {}))) {
            updateProfile('alerts', { ...profile, mappings });
        }
        return { ...mappings };
    };

    const setAndStashMetadataMappings = useCallback((s, m) => {
        if (m && IsSameMappings(s, m)) return s;
        const mappings = MetadataService.doColumnMapping(m);
        const profile = getProfile('metadata');
        if (!IsSameMappings(mappings, (profile.mappings || {}))) {
            updateProfile('metadata', { ...profile, mappings });
        }
        return { ...mappings };
    }, [getProfile, updateProfile]);

    const [alertMappings, setAlertMappings] = useReducer(setAndStashAlertMappings, DefaultAlertMappings);
    const [mtdtMappings, setMtdtMappings] = useReducer(setAndStashMetadataMappings, DefaultMtdtMappings);

    // Don't know where this went but it's needed
    useEffect(() => {
        SystemService.getSystemSettings().then((res) => {
            setSystemSettings(res);
        });
    }, []);

    const updateShowIP = useCallback(showip => {
        const profile = getProfile('iptool');
        updateProfile('iptool', { ...profile, showip });
        setShowIP(showip);
    }, [getProfile, updateProfile]);

    const updateAsset = useCallback(({ orig, ip, name, data }) => {
        if (assetPerms.write) {
            setAssets(current => {
                delete current[orig];
                return { ...current, [ip]: { ip, name, data } };
            });
            return AssetsService.updateAsset(orig || ip, { ip, name, data }, cmc.name);
        }
    }, [cmc]);

    const createAsset = useCallback(({ ip, name }) => {
        if (assetPerms.write) {
            setAssets(current => {
                return { ...current, [ip]: { ip, name, data: {} } };
            });
            return AssetsService.createAsset({ ip, name, data: {} }, cmc.name);
        }
    }, [cmc]);

    // updates from context (deleted in system/assets)
    // throttle, potentially very long list
    const refreshAssets = useCallback(async () => {
        if (!cmc) {
            setAssets();
            return;
        }
        if (assetPerms.read) {
            AssetsService.getAssetsMap('', cmc.name).then(a => {
                setAssets(a.data || {});
            }, err => {
                console.error('Unable to get access map ::: ', err);
            });
        }
    }, [cmc]);

    const deleteAsset = useCallback(ip => {
        if (assetPerms.delete) {
            setAssets(current => {
                delete current[ip];
                return current;
            });
            return AssetsService.deleteAsset(ip, cmc.name);
        }
    }, [cmc]);

    const upsertTag = useCallback(async tag => {
        if (!cmc) return;
        if (tagPerms.write) {
            TagsService.upsert(tag, cmc.name).then(() => {
                TagsService.list(false).then(t => setTags(t));
            });
        }
    }, [cmc]);

    const deleteTag = useCallback(async tag => {
        if (tagPerms.delete) {
            TagsService.delete(tag, cmc.name).then(() => {
                TagsService.list(false).then(t => setTags(t));
            });
        }
    }, [cmc]);

    useEffect(() => {
        if (!cmc) return;

        if (tagPerms.read) {
            TagsService.list(true).then(t => setTags(t));
        }
        refreshAssets();
    }, [cmc, refreshAssets]);

    const updateShowTag = useCallback(showtag => {
        const profile = getProfile('tagtool');
        updateProfile('tagtool', { ...profile, showtag });
        setShowTag(showtag);
    }, [getProfile, updateProfile]);

    const changeTheme = themename => {
        const t = themename === 'light' ? Themes.LightTheme : Themes.DarkTheme;
        setTheme(current => {
            return { ...t, date: { ...current.date, ...getProfile('date') } };
        });

        // update cookie
        Auth.updateTheme(themename);
    };

    const changeDateFormat = formats => {
        // const date = { ...theme.date, ...formats };
        updateProfile('date', formats);
        setTheme(current => {
            return { ...current, date: { ...current.date, ...formats } };
        });

        // update cookie
        Auth.updateDates(formats);
    };

    const updateSystemSettings = data => {
        SystemService.updateSystemSettings(data).then(() => {
            setSystemSettings(data);
            toast.success('System updated successfully.');
        }, error => {
            console.log(error);
            toast.error('Failed to update System settings...');
        });
    };

    const setAlertMitre = AlertsService.setAlertMitre;
    const setMtdtMitre = MetadataService.setMtdtMitre;
    const clearAlertMitre = AlertsService.clearAlertMitre;
    const clearMtdtMitre = MetadataService.clearMtdtMitre;
    const setAlertsMitre = AlertsService.setAlertsMitre;
    const setMtdtsMitre = MetadataService.setMtdtsMitre;
    const clearAlertsMitre = AlertsService.clearAlertsMitre;
    const clearMtdtsMitre = MetadataService.clearMtdtsMitre;

    useEffect(() => {
        let stopNotification;
        let retry = 0;
        if (healthPerms.read) {
            NotifyService.getNotifications().then((res) => {
                // defense against the dark arts of permissions
                setSystemHealth({ ...{ health_metrics: {}, health_status: {} }, ...res });
                stopNotification = setInterval(() => {
                    if (retry >= 3) {
                        console.log('too many attempts to get health, service may be down');
                        stopNotification && clearInterval(stopNotification);
                        Auth.boot();
                    }
                    NotifyService.getNotifications().then((res) => {
                        if (!res) return;
                        retry = 0;
                        setSystemHealth(res);
                    }, (err) => {
                        console.error(err);
                        retry++;
                    });
                }, NotifyService.HEALTH_POLL_INTERVAL);
            }, (err) => {
                console.error(err);
                Auth.boot();
            });
        }

        const iptoolProfile = getProfile('iptool');
        setShowIP(!!iptoolProfile.showip);

        const tagtoolProfile = getProfile('tagtool');
        setShowTag(!!tagtoolProfile.showtag);

        const themename = TranslateTheme(getProfile('colorscheme')) || 'dark';
        const date = getProfile('date');
        const datheme = themename === 'light' ? Themes.LightTheme : Themes.DarkTheme;
        setTheme({ ...datheme, date: { ...datheme.date, ...date } });

        // no need to stash this update to profile
        _setSidenav(getProfile('nav'));

        // Konami Code, shhhhh....
        (() => {
            const s = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a', 'Enter'];
            let cs = [];
            const tf = () => { cs = [] };
            let t = setTimeout(tf, 1500);

            window.addEventListener('keydown', e => {
                clearTimeout(t);
                cs.push(e.key);

                const eq = s.length === cs.length && s.every((v, i) => v === cs[i]);
                if (eq) {
                    toast.dark('You found a secret!');
                }
                t = setTimeout(tf, 1500);
            });
        })();

        // cmc
        if (cmcPerms.read) {
            (async () => {
                CMCsDataService.list().then(res => {
                    if ((res || []).length > 0) {
                        // get first non-standby cmc
                        const first = res.find(c => c.standby !== 'Standby');
                        if (first) {
                            setCmc({ uuid: first.uuid, name: first.name });
                        } else {
                            console.log('no active cmc found', res);
                        }

                        // fetch sensor list.
                        // standby cmc will be omitted unless requested specifically
                        SensorsService.getNames().then(names => {
                            setSensorNames(names);
                        });
                    }
                });
            })();
        }

        if (incidentPerms.read) {
            // mitre
            (async () => {
                IncidentService.getMitreCategories().then(a => {
                    setMitreCategories(a);
                });
            })();
            (async () => {
                IncidentService.getMitreCategoriesMap().then(a => {
                    setMitreCategoriesMap(a);
                });
            })();
        }

        // fetch ui
        (async () => {
            UISettings.fetchUI(true).then(data => {
                if (!data) return;
                setMessages(data.messages || {});
                setSystemSettings({ ui: data });
            });
        })();

        // column mapping
        setAlertMappings((getProfile('alerts') || {}).mappings);
        setMtdtMappings((getProfile('metadata') || {}).mappings);

        return () => {
            console.log('Thank You Come Again');
            stopNotification && clearInterval(stopNotification);
        };
    }, [getProfile]);

    // sidenav.sidebar has three states - undefined, 'open' or 'collapsed'
    useEffect(() => {
        let o = 60;
        if (!sidenav.collapsed) o = 300;
        if (sidenav.sidebar === 'open') o += 300;
        else if (sidenav.sidebar === 'collapsed') o += 60;
        setOffset(o);
    }, [sidenav]);

    // these are for convenience (and backward compatibility)
    // they are also gettable from the generic systemSettings context object.
    useEffect(() => {
        const settings = { ui: {}, ...systemSettings };
        setMessages(settings.ui.messages || {});
        setLookups((settings.ui.lookups || []).filter(d => d.url));
        setFilehashLookups((settings.ui.file_lookups || []).filter(d => d.url));
    }, [systemSettings]);

    useEffect(() => {
        if (!systemHealth) return;

        (systemHealth.notices || []).forEach(notice => {
            switch (notice.severity) {
                case 'info':
                    toast.info(<NoticeToast notice={notice}/>,
                        { toastId: notice.uuid, autoClose: 11000 });
                    break;
                case 'success':
                    toast.success(<NoticeToast notice={notice}/>,
                        { toastId: notice.uuid, autoClose: 11000 });
                    break;
                case 'warn':
                    toast.warn(<NoticeToast notice={notice}/>,
                        { toastId: notice.uuid, autoClose: false });
                    break;
                case 'critical':
                    toast.error(<NoticeToast notice={notice}/>,
                        { toastId: notice.uuid, autoClose: false });
                    break;
            }
        });
    }, [systemHealth]);

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
                <MuiThemeProvider theme={theme}>
                    <MC2ThemeChanger.Provider value={{ changeTheme, changeDateFormat }}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <CmcContext.Provider value={{ cmc, setCmc }}>
                                <DataContext.Provider value={{
                                    assets,
                                    updateAsset,
                                    createAsset,
                                    deleteAsset,
                                    refreshAssets,
                                    showIP,
                                    updateShowIP,
                                    lookups,
                                    filehashLookups,
                                    tags,
                                    upsertTag,
                                    deleteTag,
                                    showTag,
                                    updateShowTag,
                                    mitreCategories,
                                    mitreCategoriesMap,
                                    setAlertMitre,
                                    setMtdtMitre,
                                    clearAlertMitre,
                                    clearMtdtMitre,
                                    setAlertsMitre,
                                    setMtdtsMitre,
                                    clearAlertsMitre,
                                    clearMtdtsMitre,
                                    sensorNames,
                                    alertMappings,
                                    setAlertMappings,
                                    mtdtMappings,
                                    setMtdtMappings,
                                    systemHealth,
                                    setSystemHealth,
                                    systemSettings,
                                    setSystemSettings,
                                    updateSystemSettings,
                                    sidenav,
                                    setSidenav,
                                }}>
                                    <React.Fragment>
                                        <CssBaseline />
                                        <Notifications />
                                        {messages.header && <HeaderMessage />}
                                        {messages.footer && <FooterMessage />}
                                        <Copyright />
                                        <TopNav header={Boolean(messages.header)} />
                                        {!location.pathname.match('eula') &&
                                        <SideNav header={Boolean(messages.header)} path={location.pathname}/>}
                                        {!location.pathname.match('eula') &&
                                        <Breadcrumbs
                                            collapsed={sidenav.collapsed && sidenav.sidebar !== 'open'}
                                            offset={offset}
                                            path={location.pathname}
                                            header={Boolean(messages.header)}
                                        />}
                                        <main className={classes.content} style={{
                                            marginLeft: (!location.pathname.match('eula') && offset),
                                        }}>
                                            <Outlet />
                                        </main>
                                    </React.Fragment>
                                </DataContext.Provider>
                            </CmcContext.Provider>
                        </LocalizationProvider>
                    </MC2ThemeChanger.Provider>
                </MuiThemeProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

App.propTypes = {
    children: PropTypes.node,
    location: PropTypes.object,
};

const MC2ThemeChanger = React.createContext({
    changeTheme: theme => {},
    changeDateFormat: formats => {},
});

export {
    App as default,
    AppWrapper,
    MC2ThemeChanger
};
